import {formatDate} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {adbPct, adbUsdCompact, adbUsdFull} from '@features/admin-panel/helpers/admin-dashboard-format';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillView} from '@features/admin-panel/models/admin-dashboard-drill.models';
import {AdminDashboardBillingDrill} from '@features/admin-panel/models/admin-dashboard-licences.models';
import {
  AdminDashboardReportBlock,
  AdminDashboardReportProvider,
  AdminDashboardReportRow,
  AdminDashboardReportStat
} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {
  AdminDashboardBilling,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** The card's title, on screen and in the PDF report (OCA-E-159 DetailSections:324). */
const TITLE = 'Billing transactions this month';

/**
 * Billing transactions this month: the real money (successful Stripe Debits) the in-scope billing companies paid
 * since the first of the current UTC month — the total and the number of transactions as a sentence, compared with
 * the same days last month (ITP-E-160-05), and the top 5 companies by amount as green bars. The transaction count
 * opens the month's transactions, largest first; Evidence there emits `companySelected`. It means the same thing in
 * both scopes. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-billing-this-month',
  templateUrl: './admin-dashboard-billing-this-month.component.html',
  styleUrls: ['./admin-dashboard-billing-this-month.component.scss']
})
export class AdminDashboardBillingThisMonthComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** Evidence was asked for a company in the drill: the parent opens its why panel. */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = TITLE;
  readonly failedTitle = 'Couldn\'t load billing transactions this month';
  billing: AdminDashboardBilling | null = null;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private loadSubscription: Subscription;

  constructor(
    private dataService: AdminDashboardDataService,
    private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>,
    private drill: AdminDashboardDrillService
  ) {}

  /** "1 Oct": the first day of the current UTC month, where the server's sum starts. */
  get monthStart(): string {
    const now = new Date();
    return formatDate(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1), 'd MMM', 'en-US', 'UTC');
  }

  /** "3 Oct": today's UTC date, where the month's sum ends. */
  get today(): string {
    return formatDate(Date.now(), 'd MMM', 'en-US', 'UTC');
  }

  /**
   * "▲ 12.3% vs the same days last month" / "▼ 4.0% vs the same days last month" (OCA-E-159 DetailSections:59-63,
   * 325-327); "nothing billed in the same days last month" when those days summed to 0; null while the server sends
   * no previousTotal (the R4 sentence then ends at the month start).
   */
  get comparisonText(): string | null {
    const previous = this.billing?.previousTotal;
    if (!this.billing || previous === null || previous === undefined) {
      return null;
    }
    if (!previous) {
      return 'nothing billed in the same days last month';
    }
    const change = (this.billing.total - previous) / previous;
    return `${change >= 0 ? '▲' : '▼'} ${adbPct(Math.abs(change), 1)} vs the same days last month`;
  }

  /**
   * "$12.3k across 7 transactions since 1 Oct · ▲ 12.2% vs the same days last month." (OCA-E-159
   * DetailSections:325-328). Before the first response and when the load failed: "Successful payments since
   * 1 Oct."
   */
  get description(): string {
    if (!this.billing) {
      return `Successful payments since ${this.monthStart}.`;
    }
    const comparison = this.comparisonText;
    return `${adbUsdCompact(this.billing.total)} across ${countOf(this.billing.count, 'transaction')} since ${this.monthStart}`
      + `${comparison ? ' · ' + comparison : ''}.`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('licences-revenue', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  usdCompact(value: number): string {
    return adbUsdCompact(value ?? 0);
  }

  usdFull(value: number): string {
    return adbUsdFull(value ?? 0);
  }

  /** A bar's length in % of the track: its share of the largest amount; a paid amount never drops below 2 %. */
  barWidth(amount: number): number {
    const max = Math.max(0, ...(this.billing?.top ?? []).map(company => company.amount ?? 0));
    if (!max || !amount) {
      return 0;
    }
    return Math.max(2, (amount / max) * 100);
  }

  /**
   * The transaction count was clicked (OCA-E-159 DetailSections:268-281, one list for the whole month — Q4): the
   * drill lists this month's payments, largest first (ITP-E-160-04). Evidence there emits `companySelected`.
   */
  openTransactions(): void {
    if (!this.params || !this.billing?.count) {
      return;
    }
    const params = this.params;
    this.drill.open({
      title: `Billing transactions — ${this.monthStart} to ${this.today}`,
      columns: [{header: 'Date', align: 'end'}, {header: 'Amount', align: 'end'}],
      load: () => this.dataService.getWithExtra<AdminDashboardBillingDrill>('AdminDashboardBillingDrillQuery', params, {})
        .pipe(map(response => this.drillView(response)))
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** The drill's view: "3 transactions, $1,750. Largest first; the full ledger is in Billing.", a row per payment. */
  drillView(response: AdminDashboardBillingDrill): AdminDashboardDrillView {
    const rows = Array.isArray(response?.Rows) ? response.Rows : [];
    return {
      subtitle: `${countOf(response?.Total ?? 0, 'transaction')}, ${adbUsdFull(response?.Amount ?? 0)}. `
        + 'Largest first; the full ledger is in Billing.',
      rows: rows.map(row => adbDrillRowView(row, [
        {text: formatDate(row.date, 'd MMM', 'en-US', 'UTC')},
        {text: adbUsdFull(row.amount)}
      ])),
      total: response?.Total ?? rows.length
    };
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-10). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = TITLE;
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = this.description;
    const billing = this.billing;
    if (!billing) {
      return [];
    }
    const stats: AdminDashboardReportStat[] = [
      {label: 'Total', value: adbUsdFull(billing.total), detail: countOf(billing.count, 'transaction')}
    ];
    if (billing.previousTotal !== null && billing.previousTotal !== undefined) {
      stats.push({
        label: 'Same days last month',
        value: adbUsdFull(billing.previousTotal),
        detail: countOf(billing.previousCount ?? 0, 'transaction')
      });
    }
    const summary: AdminDashboardReportBlock = {kind: 'stats', title, subtitle, stats};
    if (!billing.top.length) {
      return [summary, {kind: 'empty', title: 'Top companies by amount', subtitle: 'this month', text: 'No payments this month'}];
    }
    return [summary, {
      kind: 'table',
      title: 'Top companies by amount',
      subtitle: 'this month',
      columns: [{header: 'Company', weight: 3}, {header: 'Amount', align: 'right'}],
      rows: billing.top.map((company): AdminDashboardReportRow => ({cells: [company.companyName, adbUsdFull(company.amount)]}))
    }];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardBilling>(
      'AdminDashboardBillingQuery', this.params, 'Billing'
    ).subscribe({
      next: billing => this.show(billing ? {...billing, top: Array.isArray(billing.top) ? billing.top : []} : null, false),
      error: () => this.show(null, true)
    });
  }

  /** "No payments this month" has nothing to show: it reports empty. */
  private show(billing: AdminDashboardBilling | null, failed: boolean): void {
    this.billing = billing;
    this.failed = failed;
    this.loaded = true;
    this.report(failed ? 'failed' : billing?.top.length ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
